/**
 * FikoComfyAPI — ComfyUI (img2-3d) istemcisi.
 *
 * AKIS: resim yukle -> workflow'u yamala -> /prompt -> WS ilerleme -> history -> GLB indir.
 *
 * NOT: fikoyt.com HTTPS/443 uzerinden yayinlandigi icin tarayicidan dogrudan
 * http://...:8188 cagrilamaz (mixed-content). Bu yuzden proxy.conf.js icindeki
 * "/comfyapi" yolu kullanilir -> http://127.0.0.1:8188
 * Farkli bir sunucu icin: localStorage.setItem('fikoComfyTaban','https://xxx:8188')
 */

export interface FikoComfyDosya { filename: string; subfolder: string; type: string; }

export interface FikoComfyUretimSecenek {
  /** Arka plani otomatik sil (ComfySwitchNode: "Switch: Remove background") */
  arkaPlanSil?: boolean;
  /** Doku cozunurlugu (PrimitiveInt: "Texture Resolution") — 1024 / 2048 / 4096 */
  dokuCozunurluk?: number;
  /** Her uretimde farkli sonuc icin seed'leri rastgele yap */
  rastgeleSeed?: boolean;
  /** Ilerleme geri bildirimi */
  ilerleme?: (mesaj: string, yuzde: number) => void;
  /** Iptal icin */
  iptalSinyali?: AbortSignal;
}

export interface FikoComfySonuc { blob: Blob; ad: string; uzanti: string; }

const AKIS_YOLU = '/comfy/FikoYT-img2-3d.api.json';
const UC_BOYUT_UZANTILAR = ['glb', 'gltf', 'obj', 'stl', 'ply', 'fbx', 'usdz'];

export class FikoComfyAPI {

  /** ComfyUI kok adresi (proxy yolu veya tam URL). */
  static taban(): string {
    try {
      const t = localStorage.getItem('fikoComfyTaban');
      if (t) return t.replace(/\/+$/, '');
    } catch { /* localStorage kapali olabilir */ }
    return '/comfyapi';
  }

  /**
   * Sayfa/sekme kapanirken ya da yenilenirken bile teslim garantili iptal sinyali.
   * Normal `fetch` sayfa kapanirken (beforeunload/pagehide) yarida kesilebilir;
   * `navigator.sendBeacon` tarayici tarafindan kuyruga alinip sayfa kapansa da yollanir.
   */
  static iptalBeaconGonder(): boolean {
    try {
      if (!('sendBeacon' in navigator)) return false;
      return navigator.sendBeacon(this.url('/api/interrupt'));
    } catch { return false; }
  }

  private static url(yol: string): string {
    return this.taban() + yol;
  }

  private static wsUrl(clientId: string): string {
    const t = this.taban();
    if (/^https?:\/\//i.test(t)) return t.replace(/^http/i, 'ws') + '/ws?clientId=' + clientId;
    const p = location.protocol === 'https:' ? 'wss://' : 'ws://';
    return p + location.host + t + '/ws?clientId=' + clientId;
  }

  /** Workflow'un API formatini yukler (public/comfy/...). */
  static async akisYukle(): Promise<any> {
    const r = await fetch(AKIS_YOLU, { cache: 'no-cache' });
    if (!r.ok) throw new Error('Is akisi bulunamadi: ' + AKIS_YOLU + ' (' + r.status + ')');
    return await r.json();
  }

  /** ComfyUI input klasorune resim yukler. */
  static async resimYukle(dosya: File, altKlasor = 'fikoyt-3d'): Promise<FikoComfyDosya> {
    const fd = new FormData();
    fd.append('image', dosya, dosya.name);
    fd.append('overwrite', 'true');
    fd.append('subfolder', altKlasor);
    const r = await fetch(this.url('/api/upload/image'), { method: 'POST', body: fd });
    if (!r.ok) throw new Error('Resim yuklenemedi (' + r.status + '): ' + await r.text());
    const j = await r.json();
    return { filename: j.name, subfolder: j.subfolder || '', type: j.type || 'input' };
  }

  /** class_type'a gore node id bulur. */
  private static nodeBul(prompt: any, sinif: string, baslik?: string): string | null {
    for (const id of Object.keys(prompt)) {
      const n = prompt[id];
      if (n?.class_type !== sinif) continue;
      if (baslik && n?._meta?.title !== baslik) continue;
      return id;
    }
    return null;
  }

  /** Workflow'u secilen resim ve secenekler ile yamalar. */
  private static yamala(prompt: any, resim: FikoComfyDosya, s: FikoComfyUretimSecenek): void {
    const loadId = this.nodeBul(prompt, 'LoadImage');
    if (!loadId) throw new Error('Is akisinda LoadImage node bulunamadi.');
    prompt[loadId].inputs.image =
      (resim.subfolder ? resim.subfolder + '/' : '') + resim.filename;

    if (s.arkaPlanSil !== undefined) {
      const sw = this.nodeBul(prompt, 'ComfySwitchNode', 'Switch: Remove background');
      if (sw) prompt[sw].inputs.switch = !!s.arkaPlanSil;
    }

    if (s.dokuCozunurluk) {
      const tex = this.nodeBul(prompt, 'PrimitiveInt', 'Texture Resolution');
      if (tex) prompt[tex].inputs.value = s.dokuCozunurluk;
    }

    if (s.rastgeleSeed) {
      for (const id of Object.keys(prompt)) {
        const inp = prompt[id]?.inputs;
        if (!inp) continue;
        if (typeof inp.seed === 'number') inp.seed = Math.floor(Math.random() * 1e15);
        if (typeof inp.noise_seed === 'number') inp.noise_seed = Math.floor(Math.random() * 1e15);
      }
    }
  }

  /**
   * history ciktilarindan 3D dosya referansini cikarir.
   *
   * KRITIK: Is akisinda BIRDEN FAZLA MeshToFile3D var (ham VoxelToMesh mesh'i,
   * ara adimlar, final). Bunlar ComfyUI temp klasorune "preview3d_advanced_*.glb"
   * olarak yazilir. FINAL dosya yalnizca Save3DAdvanced'in ciktisidir
   * (output/3d/ComfyUI_*.glb). outputs'un anahtar sirasi garanti degildir; bu yuzden
   * once oncelikli node id'lerine, sonra type='output' olanlara bakilir.
   */
  private static ciktiBul(outputs: any, oncelikliIdler: string[] = []): FikoComfyDosya | null {
    const dene = (v: any): FikoComfyDosya | null => {
      if (typeof v === 'string') {
        // "3d/ComfyUI_00001_.glb" veya "3d/ComfyUI_00001_.glb [output]"
        const m = v.match(/^(.*?)(?:\s*\[(\w+)\])?$/);
        if (!m) return null;
        const yol = (m[1] || '').trim();
        const tip = m[2] || 'output';
        const uz = yol.split('.').pop()?.toLowerCase() || '';
        if (!UC_BOYUT_UZANTILAR.includes(uz)) return null;
        const i = yol.lastIndexOf('/');
        return {
          filename: i >= 0 ? yol.slice(i + 1) : yol,
          subfolder: i >= 0 ? yol.slice(0, i) : '',
          type: tip
        };
      }
      if (v && typeof v === 'object' && typeof v.filename === 'string') {
        const uz = v.filename.split('.').pop()?.toLowerCase() || '';
        if (!UC_BOYUT_UZANTILAR.includes(uz)) return null;
        return { filename: v.filename, subfolder: v.subfolder || '', type: v.type || 'output' };
      }
      return null;
    };

    // Tek bir node'un ciktisindan 3D dosya cikar.
    const nodedanAl = (o: any): FikoComfyDosya | null => {
      for (const k of Object.keys(o || {})) {
        const dizi = Array.isArray(o[k]) ? o[k] : [o[k]];
        for (const v of dizi) {
          const b = dene(v);
          if (b) return b;
        }
      }
      return null;
    };

    // 1) Oncelikli node'lar (Save3DAdvanced ve onu besleyen node) — FINAL dosya burada.
    for (const id of oncelikliIdler) {
      if (!id || !outputs?.[id]) continue;
      const b = nodedanAl(outputs[id]);
      if (b) return b;
    }

    // 2) type='output' olanlar (temp'teki ara onizlemeler elenir).
    const hepsi: FikoComfyDosya[] = [];
    for (const nodeId of Object.keys(outputs || {})) {
      const b = nodedanAl(outputs[nodeId]);
      if (b) hepsi.push(b);
    }
    const kalici = hepsi.find(d => (d.type || 'output') === 'output');
    if (kalici) return kalici;

    // 3) Son care: ne bulduysak.
    return hepsi[0] || null;
  }

  /** ComfyUI output/temp klasorunden dosya indirir. */
  static async dosyaIndir(d: FikoComfyDosya): Promise<Blob> {
    const q = new URLSearchParams({
      filename: d.filename, subfolder: d.subfolder || '', type: d.type || 'output'
    });
    const r = await fetch(this.url('/api/view?' + q.toString()));
    if (!r.ok) throw new Error('3D dosya indirilemedi (' + r.status + ')');
    return await r.blob();
  }

  /** Kuyruga atar, prompt_id doner. */
  private static async kuyrugaAt(prompt: any, clientId: string): Promise<string> {
    const r = await fetch(this.url('/api/prompt'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt, client_id: clientId })
    });
    const govde = await r.text();
    if (!r.ok) throw new Error('Is akisi reddedildi (' + r.status + '): ' + govde.slice(0, 600));
    const j = JSON.parse(govde);
    if (j.node_errors && Object.keys(j.node_errors).length) {
      throw new Error('Node hatasi: ' + JSON.stringify(j.node_errors).slice(0, 600));
    }
    return j.prompt_id;
  }

  private static async gecmisAl(promptId: string): Promise<any> {
    const r = await fetch(this.url('/api/history/' + promptId));
    if (!r.ok) return null;
    const j = await r.json();
    return j?.[promptId] || null;
  }

  /** Calismanin bitmesini bekler (WS; kopmada polling'e duser). */
  private static bekle(promptId: string, clientId: string, s: FikoComfyUretimSecenek): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let bitti = false;
      let ws: WebSocket | null = null;
      let zaman: any = null;

      const kapat = () => {
        if (zaman) clearInterval(zaman);
        try { ws?.close(); } catch { /* yoksay */ }
      };
      const tamam = () => { if (!bitti) { bitti = true; kapat(); resolve(); } };
      const hata = (e: any) => { if (!bitti) { bitti = true; kapat(); reject(e); } };

      s.iptalSinyali?.addEventListener('abort', () => {
        fetch(this.url('/api/interrupt'), { method: 'POST' }).catch(() => { });
        hata(new Error('Iptal edildi'));
      });

      try {
        ws = new WebSocket(this.wsUrl(clientId));
        ws.onmessage = (ev) => {
          if (typeof ev.data !== 'string') return;   // binary preview — yoksay
          let m: any; try { m = JSON.parse(ev.data); } catch { return; }
          const d = m?.data || {};
          if (d.prompt_id && d.prompt_id !== promptId) return;

          if (m.type === 'progress' && d.max) {
            const oran = Math.min(1, (d.value || 0) / d.max);
            s.ilerleme?.('Uretiliyor… (' + (d.value || 0) + '/' + d.max + ')', 15 + oran * 70);
          } else if (m.type === 'executing') {
            if (d.node) s.ilerleme?.('Isleniyor: ' + d.node, -1);
            else if (d.prompt_id === promptId) tamam();      // node=null -> bitti
          } else if (m.type === 'execution_success') {
            tamam();
          } else if (m.type === 'execution_error') {
            hata(new Error('ComfyUI hatasi: ' + (d.exception_message || d.node_type || 'bilinmiyor')));
          } else if (m.type === 'execution_interrupted') {
            hata(new Error('Uretim kesildi'));
          }
        };
      } catch { /* WS kurulamadi — polling devrede */ }

      // Guvenlik agi: WS kopsa/mesaj kacsa bile history'den bitisi yakala
      zaman = setInterval(async () => {
        try {
          const g = await this.gecmisAl(promptId);
          const durum = g?.status?.status_str;
          if (durum === 'error') return hata(new Error('ComfyUI: is akisi hata ile bitti'));
          if (g?.status?.completed || (g?.outputs && Object.keys(g.outputs).length)) tamam();
        } catch { /* yoksay */ }
      }, 3000);
    });
  }

  /**
   * FINAL ciktiyi veren node id'leri (oncelik sirasiyla):
   * 1) Save3DAdvanced (veya Save3D/SaveGLB turevi) — tam cozunurluklu son dosya
   * 2) onu besleyen model_3d node'u (son MeshToFile3D)
   * Boylece ham VoxelToMesh onizlemesi asla secilmez.
   */
  private static finalNodeIdleri(prompt: any): string[] {
    const idler: string[] = [];
    let kaydetId: string | null = null;
    for (const id of Object.keys(prompt || {})) {
      const s = prompt[id]?.class_type || '';
      if (/^Save3D/i.test(s) || /^SaveGLB/i.test(s)) { kaydetId = id; break; }
    }
    if (kaydetId) {
      idler.push(kaydetId);
      const kaynak = prompt[kaydetId]?.inputs?.model_3d;
      if (Array.isArray(kaynak) && typeof kaynak[0] === 'string') idler.push(kaynak[0]);
    }
    return idler;
  }

  /**
   * Resimden 3D model uretir ve dosyayi (GLB — dokulu/renkli) dondurur.
   */
  static async uret(resimDosyasi: File, secenek: FikoComfyUretimSecenek = {}): Promise<FikoComfySonuc> {
    const s = secenek;
    s.ilerleme?.('ComfyUI is akisi yukleniyor…', 3);
    const prompt = await this.akisYukle();

    s.ilerleme?.('Resim gonderiliyor…', 6);
    const resim = await this.resimYukle(resimDosyasi);

    this.yamala(prompt, resim, s);

    const clientId = 'fikoyt-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    s.ilerleme?.('Kuyruga alindi…', 10);
    const promptId = await this.kuyrugaAt(prompt, clientId);

    await this.bekle(promptId, clientId, s);

    s.ilerleme?.('Model indiriliyor…', 90);
    let g = await this.gecmisAl(promptId);
    for (let i = 0; i < 10 && !g?.outputs; i++) {          // history biraz gecikebilir
      await new Promise(r => setTimeout(r, 700));
      g = await this.gecmisAl(promptId);
    }
    const dosya = this.ciktiBul(g?.outputs, this.finalNodeIdleri(prompt));
    if (!dosya) throw new Error('Is akisi bitti ama 3D cikti bulunamadi (Save3DAdvanced).');

    const blob = await this.dosyaIndir(dosya);
    s.ilerleme?.('Hazir', 100);
    return {
      blob,
      ad: dosya.filename,
      uzanti: (dosya.filename.split('.').pop() || 'glb').toLowerCase()
    };
  }
}
